/** @jsx jsx */
import { css, jsx } from '@emotion/react';
import { motion } from 'framer-motion';
import { transparentize } from 'polished';
import { useCallback, useRef, useState } from 'react';
import { formatNumber } from 'utilities/formatters';
import { Colors, Variables } from 'styles/variables';

// Animation duration
const animationDuration = 0.5; // second

// Creates one of two animated nodes for the treemap
export function Node({ node, xScale, yScale, focusedDepth, focusedNodeKey, colorsByKey, setFocusedNode }) {
	const {
		data: { key, value },
		x0,
		y0,
		x1,
		y1,
		depth,
		children,
	} = node;
	const sx0 = xScale(x0);
	const sx1 = xScale(x1);
	const sy0 = yScale(y0);
	const sy1 = yScale(y1);
	const height = sy1 - sy0;
	const width = sx1 - sx0;
	const showStats = focusedNodeKey === key && !children;
	const isNodeHidden = focusedDepth >= depth && !showStats;
	const isPrimary = focusedDepth + 1 === depth;
	const isSecondaryNode = focusedDepth + 2 === depth;

	const { isOverflowing, textContainerRef, detectOverflow } = useOverflowDetector();
	const { labelRef, countRef, scaleFont } = useScalingFont(textContainerRef);

	const handleAnimationComplete = useCallback(() => {
		if (isSecondaryNode) {
			detectOverflow();
		} else {
			scaleFont();
		}
	}, [detectOverflow, isSecondaryNode, scaleFont]);

	return (
		<motion.div
			initial={{
				x: sx0,
				y: sy0,
				height,
				width,
				opacity: 0,
			}}
			animate={{
				x: sx0,
				y: sy0,
				height,
				width,
				opacity: !isNodeHidden ? 1 : 0,
				visibility: 'visible',
				transitionEnd: {
					visibility: !isNodeHidden ? 'visible' : 'hidden',
					// Force the ending state to match perfectly (sometimes framer doesn't
					// animate x and y all the way)
					x: sx0,
					y: sy0,
				},
			}}
			onAnimationComplete={handleAnimationComplete}
			transition={{
				visibility: { from: 'visible' },
				default: { ease: 'anticipate', duration: animationDuration },
			}}
			css={leafStyles(isPrimary && !!children, colorsByKey[key] ?? '#000000')}
		>
			{showStats && (
				<div css={statsWrapper}>
					Size: {formatNumber(value)}
					<br />
					Depth: {depth} <br />
				</div>
			)}
			{!isNodeHidden &&
				(isSecondaryNode ? (
					// Secondary node
					<div
						ref={textContainerRef}
						css={[
							textStyles,
							secondaryNodeStyles,
							css`
								opacity: ${isOverflowing ? 0 : 1};
							`,
						]}
						onClick={() => {
							setFocusedNode(key);
						}}
						title={`${key} (${formatNumber(value)})`}
						cy-test="treemap-secondary-node"
					>
						{key}
					</div>
				) : (
					// Primary node
					<div
						ref={textContainerRef}
						css={[textStyles, primaryNodeStyles]}
						onClick={() => {
							setFocusedNode(key);
						}}
						style={{ ...(isPrimary && { height, width }) }}
						title={`${key} (${formatNumber(value)})`}
						cy-test="treemap-primary-node"
					>
						<span ref={labelRef} css={labelStyle}>
							{key}
						</span>{' '}
						<span ref={countRef} css={countStyle}>
							({formatNumber(value)})
						</span>
					</div>
				))}
		</motion.div>
	);
}

const leafStyles = (lightenNode, color) => css`
	background-color: ${lightenNode ? transparentize(0.3, color) : color};
	transition: background-color 200ms ease-in-out;
	${lightenNode &&
	css`
		&:hover {
			background-color: ${transparentize(0.7, color)};
		}

		/* &:active{
			background-color: ${transparentize(0.9, color)};
		} */
	`}

	border: ${Colors.LIGHT_GRAY4} solid ${lightenNode ? 2 : 1}px;
	overflow: hidden;
	border-radius: 6px;
	position: absolute;
	color: white;
`;

const textPadding = 5;
const transitionTime = 200;
const animatedTextSTyles = css`
	transition: font-size ${transitionTime}ms ease-in-out, opacity ${transitionTime}ms ease-in-out;
`;
const textStyles = css`
	fill: white;
	cursor: pointer;
	overflow: hidden;

	font-family: ${Variables.PT_FONT_FAMILY};
	padding: ${textPadding}px;

	// Used for overflow detection
	max-height: 100%;
	max-width: 100%;
`;
const secondaryNodeStyles = css`
	${animatedTextSTyles};
	font-size: 10px;
	bottom: 0;
	right: 0;
	text-align: right;
	position: absolute;
`;
const primaryNodeStyles = css`
	display: block;
`;

const fontSizeOptions = [{ label: 20, count: 14 }, { label: 14, count: 10 }, { label: 12 }, { label: 10 }, {}];

const labelStyle = css`
	${animatedTextSTyles};
	font-size: ${fontSizeOptions[0].label}px;
	display: inline-block;
`;
const countStyle = css`
	${animatedTextSTyles};
	font-size: ${fontSizeOptions[0].count}px;
	display: inline-block;
	opacity: 0.7;
`;

const statsWrapper = css`
	position: absolute;
	overflow: hidden;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	display: flex;
	justify-content: center;
	align-items: center;
`;

// Utilities
const sleep = (time = 1000) => new Promise((r) => setTimeout(r, time));
const animationWait = () =>
	new Promise((resolve) => {
		window.requestAnimationFrame(resolve);
	});

// Scales font down till it fits or is gone
function useScalingFont(textContainerRef) {
	const labelRef = useRef(null);
	const countRef = useRef(null);
	// const [effectTrigger, setEffectTrigger] = useState(0);

	const scaleFont = useCallback(() => {
		// setEffectTrigger((t) => t + 1);
		const labelEl = labelRef.current;
		const countEl = countRef.current;

		if (labelEl && countEl) {
			(async function () {
				let fontIndex = 0;
				let isOverflow = true; // blindly try the first option (important for window re-size events)
				while (isOverflow && fontIndex <= fontSizeOptions.length - 1) {
					// apply fontIndex styles
					const labelEl = labelRef.current;
					const countEl = countRef.current;

					if (labelEl && countEl) {
						const fontSizes = fontSizeOptions[fontIndex];
						applyStyles(labelEl, fontSizes.label);
						applyStyles(countEl, fontSizes.count);

						await sleep(transitionTime);
						await animationWait();

						// Check if we overflow
						isOverflow = getOverflow(textContainerRef);
					}
					fontIndex++;
				}
			})();
		}
	}, [textContainerRef]);

	return { labelRef, countRef, scaleFont };
}

function applyStyles(el, fontSize) {
	let styles = fontSize ? { fontSize: `${fontSize}px` } : { opacity: 0, fontSize: 0 };
	if (styles.opacity == null) {
		el.style.opacity = styles.opacity;
	}
	el.style.fontSize = styles.fontSize;
}

// Determines if stuff is overflowing from a div
function useOverflowDetector() {
	const textContainerRef = useRef(null);
	// Set to true initially so words are not shown until it is known that there
	// is enough space.
	const [isOverflowing, setIsOverflowing] = useState(true);

	const detectOverflow = useCallback(() => {
		(async () => {
			await animationWait();
			const isOverflow = getOverflow(textContainerRef);
			setIsOverflowing(isOverflow);
		})();
	}, []);

	return { isOverflowing, textContainerRef, detectOverflow };
}

function getOverflow(ref) {
	return (
		!!ref.current &&
		(ref.current.scrollHeight > ref.current.offsetHeight || ref.current.scrollWidth > ref.current.offsetWidth)
	);
}
